import React, { useEffect, useMemo } from "react";
import { Box, Button, Flex, HStack, Spinner, Text, VStack } from "@chakra-ui/react";
import { TradeFilterSelect } from "@/components/trade-filter-selects";
import { Field, FieldLabel } from "@/components/ui/field";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import CardHeader from "../../../components/Card/CardHeader";
import type { TradePolicyCatalogField } from "../../../types/tradePolicy";
import { reportDetailSectionCardProps } from "../Reports/components/ReportsCardChrome";
import { ComparisonRunPanel } from "./ComparisonRunPanel";
import { SimulatorBackNav } from "./SimulatorBackNav";
import {
  getComparisonRunSavedTitle,
  MAX_COMPARISON_RUNS,
  type ComparisonRun,
} from "./simulatorComparisonTypes";
import type { SimulatorPanelVariant } from "./simulatorComparisonTypes";
import {
  buildSimulationDetailMetaItems,
  SimulationDetailTitleCard,
} from "./simulationDetailCardChrome";
import {
  SimulatorComparisonRunsProvider,
  useSimulatorComparisonRuns,
} from "./simulatorComparisonContext";
import { SimulatorTradeReviewSection } from "./SimulatorTradeReviewSection";
import { SimulatorTradingPnlSection } from "./SimulatorTradingPnlSection";
import { SimulatorAlert } from "./SimulatorAlert";

const TextAny = Text as unknown as React.FC<any>;
const BoxAny = Box as unknown as React.FC<any>;
const VStackAny = VStack as unknown as React.FC<any>;
const CardAny = Card as unknown as React.FC<any>;
const CardHeaderAny = CardHeader as unknown as React.FC<any>;
const CardBodyAny = CardBody as unknown as React.FC<any>;

const SIMULATOR_RUN_SELECT_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]";

function runSelectLabel(run: ComparisonRun, maxLen = 28): string {
  const raw = getComparisonRunSavedTitle(run).trim() || "Run";
  const title = raw.length > maxLen ? `${raw.slice(0, maxLen)}…` : raw;
  const loading = run.status === "running" && run.result == null && !run.error;
  return loading ? `${title} · …` : title;
}

type ComparisonResultsSectionProps = {
  comparisonRuns: ComparisonRun[];
  fieldsByPath: Map<string, TradePolicyCatalogField>;
  borderColor: string;
  textColor: string;
  subtleText: string;
  evidencePreBg: string;
  evidencePreBorder: string;
  variant?: SimulatorPanelVariant;
  brokerAccountCode: string;
  reportingTimeZone: string;
  initialSelectedRunId?: string | null;
  backNavigation?: { label: string; onClick: () => void };
  initialBacktestLoading?: boolean;
  initialBacktestError?: string | null;
  catalogError?: string | null;
  onRemoveRun: (id: string) => void;
  onSaveRunLabel: (id: string, label: string) => void;
  onRetryRun: (id: string) => void;
  onSaveBacktest: (id: string) => void;
  onSaveAsStrategyVersion: (id: string) => void;
  linkedStrategyIdFallback?: string | null;
  /** Exit Placement: show compose-to-TradingView on result runs. */
  enableExitPlacementCompose?: boolean;
  onComposeExitPlacementForRun?: (id: string) => void;
  onActiveRunLabelChange?: (label: string | null) => void;
  simulationOverlayMode?: string;
  reportId?: string | null;
  reportCreatedAt?: string | null;
  prefillWindow?: {
    start_date?: string | null;
    end_date?: string | null;
    timeframe?: string | null;
  } | null;
  defaultCandleTimeframe?: "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d";
  linkedBacktests?: import("../../../types/backtests").Backtest[];
  linkedBacktestsRefreshKey?: number;
  /** Report embed: open unified policy pick overlap dialog (saved sims). */
  onOpenCompareOverlap?: (seedBacktestIds?: string[]) => void;
  /** Saved-simulation detail — one run, no comparison tabs or quota. */
  hideRunTabs?: boolean;
  /** Workspace drawer — show entity title card like strategy/report sheets. */
  showEntityTitleCard?: boolean;
  /** Hide in-panel Show policy; chrome row mounts the workspace policy dock card. */
  hideShowPolicyButton?: boolean;
  /** Hide in-panel Save as policy version when chrome hosts the action. */
  hideSaveAsStrategyVersionButton?: boolean;
  onRegisterOpenPolicyModal?: (open: (() => void) | null) => void;
  linkedStrategyName?: string | null;
  initialBacktestId?: string | null;
};

type ComparisonMetricsPanelProps = Omit<
  ComparisonResultsSectionProps,
  "comparisonRuns" | "initialSelectedRunId" | "brokerAccountCode" | "reportingTimeZone" | "simulationOverlayMode" | "prefillWindow" | "defaultCandleTimeframe" | "linkedBacktests" | "linkedBacktestsRefreshKey" | "reportCreatedAt"
>;

function ComparisonRunMetricsPanel(props: ComparisonMetricsPanelProps): React.ReactElement {
  const {
    comparisonRuns,
    runsForTabs,
    selectedRunId,
    setSelectedRunId,
    selectedRun,
    sharedBaseline,
    showRunTabs,
  } = useSimulatorComparisonRuns();

  const resolvedLinkedStrategyId =
    selectedRun?.linkedStrategyId?.trim() || props.linkedStrategyIdFallback?.trim() || "";
  const hasPersistedSimulation = Boolean(
    selectedRun?.backtestId?.trim() || selectedRun?.isSavedBacktest
  );
  const canSaveAsStrategyVersion = Boolean(resolvedLinkedStrategyId) && hasPersistedSimulation;
  const composeSliceIds =
    selectedRun?.exitPlacementSlices
      ?.map((s) => String(s.slice_id ?? "").trim())
      .filter(Boolean) ?? [];
  const canComposeExitPlacement = Boolean(
    props.enableExitPlacementCompose &&
      resolvedLinkedStrategyId &&
      selectedRun &&
      !selectedRun.error &&
      selectedRun.status !== "running" &&
      composeSliceIds.length > 0
  );

  useEffect(() => {
    if (!props.onActiveRunLabelChange) return;
    const label = selectedRun ? getComparisonRunSavedTitle(selectedRun) : null;
    props.onActiveRunLabelChange(label?.trim() || null);
  }, [props.onActiveRunLabelChange, selectedRun]);

  const emptyMessage =
    props.variant === "standalone" ? (
      <>
        No simulation results yet. Enter a <strong>next run title</strong>, build your policy and scope, then use{" "}
        <strong>Generate simulation</strong> from the wizard.
      </>
    ) : (
      <>
        No simulation results yet. Enter a <strong>next run title</strong> under Option 1 or Option 2, adjust the policy or picks, then use{" "}
        <strong>Run &amp; add to comparison</strong> to add a run.
      </>
    );

  const runSelectOptions = useMemo(
    () =>
      runsForTabs.map((run) => ({
        value: run.id,
        label: runSelectLabel(run),
      })),
    [runsForTabs]
  );

  const runSelect =
    showRunTabs && comparisonRuns.length > 0 ? (
      <Field className="mb-6 w-fit min-w-0 max-w-[14rem]" data-simulator-comparison-run-select="">
        <FieldLabel htmlFor="simulator-comparison-run" className={SIMULATOR_RUN_SELECT_LABEL_CLASS}>
          Choose simulation
        </FieldLabel>
        <TradeFilterSelect
          id="simulator-comparison-run"
          layout="fit"
          fitToLongestOption
          value={selectedRunId}
          onValueChange={setSelectedRunId}
          options={runSelectOptions}
          placeholder="Select simulation"
        />
      </Field>
    ) : null;

  const metricsBody =
    props.initialBacktestLoading ? (
      <HStack py={8} spacing={3} justify="center">
        <Spinner size="sm" />
        <TextAny color={props.subtleText} fontSize="sm">
          Loading simulation…
        </TextAny>
      </HStack>
    ) : props.initialBacktestError ? (
      <SimulatorAlert tone="error">{props.initialBacktestError}</SimulatorAlert>
    ) : comparisonRuns.length === 0 ? (
      <TextAny fontSize="sm" color={props.subtleText} borderWidth="1px" borderColor={props.borderColor} borderRadius="md" p={4}>
        {emptyMessage}
      </TextAny>
    ) : (
      <Box w="100%" minW={0} data-simulator-results-panel="">
        {selectedRun ? (
          <ComparisonRunPanel
            key={selectedRun.id}
            run={selectedRun}
            fieldsByPath={props.fieldsByPath}
            sharedBaseline={sharedBaseline}
            borderColor={props.borderColor}
            textColor={props.textColor}
            subtleText={props.subtleText}
            evidencePreBg={props.evidencePreBg}
            evidencePreBorder={props.evidencePreBorder}
            onRemove={() => props.onRemoveRun(selectedRun.id)}
            onSaveRunLabel={(label) => props.onSaveRunLabel(selectedRun.id, label)}
            onRetry={() => props.onRetryRun(selectedRun.id)}
            onSaveBacktest={() => props.onSaveBacktest(selectedRun.id)}
            onSaveAsStrategyVersion={() => props.onSaveAsStrategyVersion(selectedRun.id)}
            canSaveAsStrategyVersion={canSaveAsStrategyVersion}
            onComposeExitPlacement={
              props.enableExitPlacementCompose && props.onComposeExitPlacementForRun
                ? () => props.onComposeExitPlacementForRun!(selectedRun.id)
                : undefined
            }
            canComposeExitPlacement={canComposeExitPlacement}
            hideSaveSimulationAction={props.hideRunTabs || props.variant === "report"}
            hideRunTitle={props.showEntityTitleCard}
            hideShowPolicyButton={props.hideShowPolicyButton}
            hideSaveAsStrategyVersionButton={props.hideSaveAsStrategyVersionButton}
            onRegisterOpenPolicyModal={props.onRegisterOpenPolicyModal}
          />
        ) : null}
      </Box>
    );

  return (
    <>
      {runSelect}
      {metricsBody}
    </>
  );
}

const MemoizedComparisonRunMetricsPanel = React.memo(ComparisonRunMetricsPanel);

function SimulationDetailTitleCardSection({
  brokerAccountCode,
  linkedStrategyName,
  initialBacktestLoading,
  initialBacktestId,
}: {
  brokerAccountCode: string;
  linkedStrategyName?: string | null;
  initialBacktestLoading?: boolean;
  initialBacktestId?: string | null;
}): React.ReactElement | null {
  const { selectedRun } = useSimulatorComparisonRuns();

  const title = React.useMemo(() => {
    if (initialBacktestLoading) return "Loading simulation…";
    if (selectedRun) {
      const savedTitle = getComparisonRunSavedTitle(selectedRun).trim();
      return savedTitle || "Simulation";
    }
    return "Simulation";
  }, [initialBacktestLoading, selectedRun]);

  const metaItems = React.useMemo(
    () =>
      buildSimulationDetailMetaItems({
        accountCode: brokerAccountCode,
        scope: selectedRun?.scope ?? null,
        simulationId: selectedRun?.backtestId?.trim() || initialBacktestId?.trim() || null,
        createdAtMs: selectedRun?.createdAt ?? null,
      }),
    [brokerAccountCode, initialBacktestId, selectedRun]
  );

  const strategyName =
    linkedStrategyName?.trim() && linkedStrategyName.trim() !== "No strategy"
      ? linkedStrategyName.trim()
      : null;

  return (
    <SimulationDetailTitleCard
      title={title}
      metaItems={metaItems}
      strategyName={strategyName}
      symbol={selectedRun?.scope.symbol?.trim() || null}
    />
  );
}

function ComparisonResultsSectionInner(props: ComparisonResultsSectionProps): React.ReactElement {
  const showComparisonChrome =
    !props.hideRunTabs && props.comparisonRuns.length > 1;
  const { selectedRunId } = useSimulatorComparisonRuns();

  const seedBacktestIds = React.useMemo(() => {
    const active = props.comparisonRuns.find((r) => r.id === selectedRunId);
    if (active?.backtestId) return [active.backtestId];
    return props.comparisonRuns
      .map((r) => r.backtestId)
      .filter((id): id is string => Boolean(id?.trim()));
  }, [props.comparisonRuns, selectedRunId]);

  return (
    <BoxAny>
      {props.backNavigation ? (
        <SimulatorBackNav
          label={props.backNavigation.label}
          subtleText={props.subtleText}
          onClick={props.backNavigation.onClick}
        />
      ) : null}
      <CardAny
        {...reportDetailSectionCardProps}
        w="100%"
        minW={0}
        data-simulator-results-card=""
      >
        <CardHeaderAny pb={{ base: 3, md: 4 }}>
          {props.showEntityTitleCard ? (
            <SimulationDetailTitleCardSection
              brokerAccountCode={props.brokerAccountCode}
              linkedStrategyName={props.linkedStrategyName}
              initialBacktestLoading={props.initialBacktestLoading}
              initialBacktestId={props.initialBacktestId}
            />
          ) : (
            <VStackAny align="stretch" spacing={2} w="100%">
              <Flex align="flex-start" justify="space-between" gap={3} flexWrap="wrap" w="100%">
                <TextAny
                  fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  fontWeight="normal"
                  color="#38BBD1"
                  fontFamily="var(--font-primary)"
                  lineHeight="1.1"
                >
                  Simulation results
                </TextAny>
                <HStack spacing={2} flexWrap="wrap">
                  {props.onOpenCompareOverlap && props.variant === "report" ? (
                    <Button
                      size="sm"
                      variant="outline"
                      borderRadius="6px"
                      borderColor={props.borderColor}
                      color={props.subtleText}
                      fontFamily="var(--font-primary)"
                      fontWeight="medium"
                      textTransform="none"
                      onClick={() => props.onOpenCompareOverlap?.(seedBacktestIds)}
                      isDisabled={(props.linkedBacktests?.length ?? 0) === 0 && seedBacktestIds.length === 0}
                    >
                      Compare overlap
                    </Button>
                  ) : null}
                </HStack>
              </Flex>
              <TextAny
                data-simulator-results-description=""
                fontSize="sm"
                color={props.subtleText}
                mt={1}
                fontFamily="var(--font-primary)"
              >
                {showComparisonChrome ? (
                  <>
                    Choose a simulation below. <strong>Baseline</strong> and <strong>filtered</strong> metrics are for the
                    selected run. Policy shows as one line; expand for the full tree. Issues with a run appear as alerts at
                    the top of the card. Older runs first in the list, new runs last — up to {MAX_COMPARISON_RUNS} runs.
                  </>
                ) : (
                  <>
                    <strong>Baseline</strong> and <strong>filtered</strong> metrics for this simulation. Policy shows as one
                    line; expand for the full tree. Issues appear as alerts at the top of the card.
                  </>
                )}
              </TextAny>
            </VStackAny>
          )}
        </CardHeaderAny>
        <CardBodyAny pt={0}>
          {props.catalogError ? (
            <BoxAny mb={4}>
              <SimulatorAlert tone="error" data-simulator-catalog-error="">
                <>
                  <strong>Error.</strong> {props.catalogError}
                </>
              </SimulatorAlert>
            </BoxAny>
          ) : null}
          <MemoizedComparisonRunMetricsPanel {...props} />
        </CardBodyAny>
      </CardAny>
      {props.variant !== "report" ? (
        <>
          <SimulatorTradingPnlSection
            subtleText={props.subtleText}
            brokerAccountCode={props.brokerAccountCode}
            reportingTimeZone={props.reportingTimeZone}
            reportId={props.reportId}
            reportCreatedAt={props.reportCreatedAt}
            prefillWindow={props.prefillWindow}
            linkedBacktests={props.linkedBacktests}
          />
          <SimulatorTradeReviewSection
            subtleText={props.subtleText}
            brokerAccountCode={props.brokerAccountCode}
            reportingTimeZone={props.reportingTimeZone}
            reportId={props.reportId}
            reportCreatedAt={props.reportCreatedAt}
            prefillWindow={props.prefillWindow}
            defaultCandleTimeframe={props.defaultCandleTimeframe}
            simulationOverlayMode={props.simulationOverlayMode}
            linkedBacktests={props.linkedBacktests}
            linkedBacktestsRefreshKey={props.linkedBacktestsRefreshKey}
          />
        </>
      ) : null}
    </BoxAny>
  );
}

/** Results + trade review — trade review is a separate memoized subtree fed by comparison-run context. */
export function ComparisonResultsSection(props: ComparisonResultsSectionProps): React.ReactElement {
  return (
    <SimulatorComparisonRunsProvider
      comparisonRuns={props.comparisonRuns}
      initialSelectedRunId={props.initialSelectedRunId}
      hideRunTabs={props.hideRunTabs}
    >
      <ComparisonResultsSectionInner {...props} />
    </SimulatorComparisonRunsProvider>
  );
}
